import type { ApiError } from 'src/types/common';
import type { FilterSortParams } from 'src/types/pagination';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { DEFAULT_PAGE_SIZE } from 'src/types/pagination';

import { usePaginatedApi } from './use-paginated-api';
import { useSSRPaginatedInfiniteScrolling } from './use-ssr-paginated-infinite-scrolling';

/** Whether another page can be requested (0-based page index vs total page count). */
export function hasMoreFilterPages(page: number, totalPages: number): boolean {
  return page + 1 < totalPages;
}

function readNumericId(value: unknown): number | undefined {
  if (value != null && typeof value === 'object' && 'id' in value) {
    const id = (value as { id: unknown }).id;
    return typeof id === 'number' ? id : undefined;
  }
  return undefined;
}

export function useListForFilter<T>({
  endpoint,
  pageSize = DEFAULT_PAGE_SIZE,
  filterParams,
  additionalParams,
  initialData,
  enabled = true,
  resetPageOnSearchChange = false,
}: {
  endpoint: string;
  pageSize?: number;
  filterParams?: FilterSortParams;
  additionalParams?: Record<string, string | number | string[] | number[]>;
  initialData?: T;
  /** When false, skips the API request (e.g. until a dependent value like projectId is set) */
  enabled?: boolean;
  /** When true, resets pagination to page 1 and ignores stale pages when search changes */
  resetPageOnSearchChange?: boolean;
}): {
  onPageChange: (page: number) => void;
  page: number;
  total: number;
  onSearchChange: (value: string | null) => void;
  searchValue: string | null;
  paginatedData: T[];
  isLoading: boolean;
  /** Request failure, so callers can show an error instead of an empty list. */
  error: ApiError | null;
  /** True when more pages exist (for filter dropdown infinite scroll). */
  hasNextPage: boolean;
  onLoadMore: () => void;
} {
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const expectingPageOneAfterSearchRef = useRef(false);
  const listGenerationRef = useRef(0);
  const loadMoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: paginatedData,
    page,
    pageSize: pageSizeFromSSR,
    onPageLoaded,
    onPageChange,
    totalPages,
    total,
    onReset,
  } = useSSRPaginatedInfiniteScrolling(pageSize);

  const additionalParamsKey = JSON.stringify(additionalParams || {});
  const previousAdditionalParamsKeyRef = useRef(additionalParamsKey);

  const cancelPendingLoadMore = useCallback(() => {
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }
  }, []);

  const resetList = useCallback(() => {
    listGenerationRef.current += 1;
    expectingPageOneAfterSearchRef.current = true;
    cancelPendingLoadMore();
    onReset();
  }, [cancelPendingLoadMore, onReset]);

  useEffect(() => {
    if (previousAdditionalParamsKeyRef.current !== additionalParamsKey) {
      previousAdditionalParamsKeyRef.current = additionalParamsKey;

      if (resetPageOnSearchChange) {
        resetList();
      } else {
        onReset();
      }
    }
  }, [additionalParamsKey, onReset, resetList, resetPageOnSearchChange]);

  const { data, pagination, isLoading, error } = usePaginatedApi<T>({
    endpoint,
    paginationParams: {
      page: page + 1,
      take: pageSizeFromSSR,
    },
    filterParams: {
      search: searchValue || undefined,
      ...filterParams,
    },
    additionalParams,
    enabled,
    ...(resetPageOnSearchChange ? { keepPreviousData: false, resetPageOnSearchChange: true } : {}),
  });

  useEffect(() => {
    if (!resetPageOnSearchChange) {
      onPageLoaded(data, pagination);
      return;
    }

    const pageNum = pagination?.page ?? 1;

    if (expectingPageOneAfterSearchRef.current && pageNum !== 1) {
      return;
    }

    if (pageNum === 1) {
      expectingPageOneAfterSearchRef.current = false;
    }

    if (pageNum !== page + 1) {
      return;
    }

    onPageLoaded(data, pagination);
  }, [data, onPageLoaded, page, pagination, resetPageOnSearchChange]);

  const onSearchChange = useCallback(
    (value: string | null) => {
      const normalized = resetPageOnSearchChange
        ? value?.trim()
          ? value.trim()
          : null
        : value;

      if (normalized === searchValue) {
        return;
      }

      if (resetPageOnSearchChange) {
        resetList();
      } else if (normalized !== searchValue) {
        onReset();
      }

      setSearchValue(normalized);
    },
    [onReset, resetList, resetPageOnSearchChange, searchValue]
  );

  const onLoadMore = useCallback(() => {
    if (resetPageOnSearchChange) {
      cancelPendingLoadMore();
    }

    const generation = listGenerationRef.current;

    loadMoreTimeoutRef.current = setTimeout(() => {
      loadMoreTimeoutRef.current = null;

      if (resetPageOnSearchChange && generation !== listGenerationRef.current) {
        return;
      }

      if (hasMoreFilterPages(page, totalPages) && !isLoading) {
        onPageChange(page + 1);
      }
    }, 200);
  }, [
    cancelPendingLoadMore,
    page,
    totalPages,
    onPageChange,
    isLoading,
    resetPageOnSearchChange,
  ]);

  const paginatedDataWithInitialData = useMemo(() => {
    const newData = [...paginatedData];
    const initialId = readNumericId(initialData);
    if (initialData && initialId != null) {
      if (!newData.some((item) => readNumericId(item) === initialId)) {
        newData.unshift(initialData);
      }
    }
    const seenIds = new Set<number>();
    return newData.filter((item) => {
      const id = readNumericId(item);
      if (id == null) {
        return true;
      }
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }, [paginatedData, initialData]);

  return {
    onPageChange,
    page,
    total,
    isLoading,
    error,
    hasNextPage: pagination?.hasNextPage ?? false,
    onSearchChange,
    searchValue,
    paginatedData: paginatedDataWithInitialData,
    onLoadMore,
  };
}
